import { useState } from 'react';
import { BookingRequest, BookingUpdate } from '@/types/bookings';
import { Booking } from '@/types/calendar';
import bookingService from '@/services/bookingService';
import { auth } from '@/firebase/config'; 

export function useBookings() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingRequests, setBookingRequests] = useState<BookingRequest[]>([]);

  const bookingAction = async (formData: FormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');
      
      const newBooking = await bookingService.createBookingRequest(formData) as BookingRequest;
      
      setBookingRequests([...bookingRequests, newBooking]);
      return { bookingRequests: [...bookingRequests, newBooking] };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking request');
      return { error: err instanceof Error ? err.message : 'Failed to create booking request' };
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingRequest = async (bookingId: string, userId: string, updates: BookingUpdate) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await bookingService.updateBookingRequest(bookingId, userId, updates);
      
      // Update local state
      setBookingRequests(prev => prev.map(request => 
        request.id === bookingId 
          ? { ...request, ...updates }
          : request
      ));

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking request');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    bookingAction,
    updateBookingRequest,
    error,
    bookings,
    bookingRequests,
    isLoading
  };
}